// src/components/DistrictSlider.jsx
import React from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import { normalizeMediaUrl } from '../api/base';

const SliderWrap = styled.div`
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: var(--shadow);
  background: #000;
  margin-bottom: 24px;
  
  .swiper {
    width: 100%;
    height: 404px; /* Фиксированная высота */
  }
  
  .swiper-slide {
    position: relative;
    overflow: hidden;
    width: auto !important; /* Автоматическая ширина */
    height: 404px;
    cursor: default; /* Обычный курсор */
    
    img {
      height: 100%;
      width: auto;
      object-fit: cover;
      display: block;
      border-right: 2px solid rgba(255, 255, 255, 0.3); /* Разделитель между фотками */
    }
    
    /* Убираем разделитель у последнего слайда */
    &:last-child img {
      border-right: none;
    }
  }
  
  .swiper-button-next,
  .swiper-button-prev {
    color: var(--primary);
    background: rgba(255, 255, 255, 0.9);
    width: 44px;
    height: 44px;
    border-radius: 50%;
    margin-top: -22px;
    
    &:after {
      font-size: 18px;
      font-weight: 700;
    }
    
    &:hover {
      background: rgba(255, 255, 255, 1);
    }
    
    @media (max-width: 768px) {
      width: 36px;
      height: 36px;
      margin-top: -18px;
      
      &:after {
        font-size: 14px;
      }
    }
  }
`;

const Caption = styled.div`
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 60px;
  z-index: 2;
  
  h2 {
    margin: 0 0 8px;
    font-size: clamp(24px, 4vw, 36px);
    font-weight: 700;
    line-height: 1.2;
    color: white;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.7);
  }
  
  p {
    margin: 0;
    font-size: clamp(14px, 2.5vw, 18px);
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.9);
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.7);
    max-width: 600px;
  }
  
  @media (max-width: 768px) {
    left: 16px;
    right: 16px;
    bottom: 50px;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.1) 50%,
    rgba(0, 0, 0, 0.6) 100%
  );
  z-index: 1;
  pointer-events: none; /* Не блокируем клики */
`;



export default function DistrictSlider({ district, images = [] }) {
  const sliderImages = React.useMemo(() => {
    const allImages = [];
    
    // Добавляем дополнительные изображения из галереи
    if (Array.isArray(images) && images.length > 0) {
      images.forEach(img => {
        // Приоритет: url (оригинал) > thumbnail_url (превью)
        const imageUrl = img?.url || img?.thumbnail_url || img;
        
        if (imageUrl) {
          allImages.push({
            url: normalizeMediaUrl(imageUrl),
            title: String(img?.title || img?.alt_text || district?.name || 'Район'),
            description: img?.description || img?.caption || ''
          });
        }
      });
    }
    
    // Если нет изображений из галереи, добавляем основное изображение района
    if (allImages.length === 0 && district?.cover_url) {
      allImages.push({
        url: normalizeMediaUrl(district.cover_url),
        title: String(district?.name || 'Район'),
        description: district.short_description || district.excerpt || ''
      });
    }
    
    // Если совсем нет изображений, добавляем качественную заглушку
    if (!allImages.length) {
      allImages.push({
        url: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=1600&h=800&fit=crop&q=85',
        title: String(district?.name || 'Район'),
        description: district?.short_description || ''
      });
    }
    
    return allImages;
  }, [district, images]);

  return (
    <SliderWrap>
      <Swiper
        modules={[Navigation, FreeMode]}
        navigation={sliderImages.length > 1}
        spaceBetween={0}
        slidesPerView="auto" /* Автоматическое количество слайдов */
        freeMode={true} /* Свободное листание */
      >
        {sliderImages.map((image, index) => (
          <SwiperSlide key={index}>
            <img
              src={image.url}
              alt={image.title}
              loading={index === 0 ? 'eager' : 'lazy'}
            />
            {/* Показываем подпись только на первом слайде */}
            {index === 0 && (
              <>
                <Overlay />
                <Caption>
                  <h2>{String(image.title || '')}</h2>
                  {image.description && <p>{String(image.description)}</p>}
                </Caption>
              </>
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    </SliderWrap>
  );
}